/* ============================================
   PwaInstallBanner — Smart install prompt
   - Chrome/Android: uses beforeinstallprompt
   - Safari/iOS: shows manual instructions
   - Desktop: hidden
   - Dismissible with localStorage persistence
   ============================================ */

import { useState, useEffect, useCallback } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}

function isInStandaloneMode(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as unknown as { standalone: boolean }).standalone === true)
  )
}

function isMobileDevice(): boolean {
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent)
}

export default function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showIosInstructions, setShowIosInstructions] = useState(false)

  useEffect(() => {
    // Don't show if already installed or dismissed
    if (isInStandaloneMode()) return
    if (localStorage.getItem('pwa-banner-dismissed') === '1') return
    if (!isMobileDevice()) return

    // Chrome/Android: listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShowBanner(true)
    }
    window.addEventListener('beforeinstallprompt', handler)

    // iOS Safari: show manually after a short delay
    if (isIos()) {
      const timer = setTimeout(() => setShowBanner(true), 2000)
      return () => {
        clearTimeout(timer)
        window.removeEventListener('beforeinstallprompt', handler)
      }
    }

    // Android: show banner after a short delay even without prompt (fallback)
    const fallbackTimer = setTimeout(() => {
      if (!deferredPrompt) setShowBanner(true)
    }, 3000)

    return () => {
      clearTimeout(fallbackTimer)
      window.removeEventListener('beforeinstallprompt', handler)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      // Chrome/Android native prompt
      await deferredPrompt.prompt()
      const result = await deferredPrompt.userChoice
      if (result.outcome === 'accepted') {
        setShowBanner(false)
      }
      setDeferredPrompt(null)
    } else if (isIos()) {
      // Show iOS-specific instructions
      setShowIosInstructions(true)
    }
  }, [deferredPrompt])

  const handleDismiss = useCallback(() => {
    setShowBanner(false)
    setShowIosInstructions(false)
    localStorage.setItem('pwa-banner-dismissed', '1')
  }, [])

  if (!showBanner || isInStandaloneMode()) return null

  return (
    <div className="pwa-banner">
      <div className="pwa-banner-content">
        <div className="pwa-banner-icon">
          <img src="/assets/brand/pwa-icon-192.png" alt="1Conecta" className="pwa-banner-app-icon" />
        </div>
        <div className="pwa-banner-text">
          <strong className="pwa-banner-title">Instala 1Conecta</strong>
          <p className="pwa-banner-desc">
            Accede más rápido desde tu pantalla de inicio
          </p>
        </div>
        <div className="pwa-banner-actions">
          <button className="pwa-banner-install" onClick={handleInstall}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Instalar
          </button>
          <button className="pwa-banner-dismiss" onClick={handleDismiss} aria-label="Cerrar">
            ✕
          </button>
        </div>
      </div>

      {/* iOS Instructions Overlay */}
      {showIosInstructions && (
        <>
          <div className="pwa-ios-overlay" onClick={() => setShowIosInstructions(false)} />
          <div className="pwa-ios-instructions">
            <button className="pwa-ios-close" onClick={() => setShowIosInstructions(false)}>✕</button>
            <h3>Instalar en iPhone/iPad</h3>
            <ol className="pwa-ios-steps">
              <li>
                <span className="pwa-ios-step-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d2d3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </span>
                Toca el botón <strong>Compartir</strong> en Safari
              </li>
              <li>
                <span className="pwa-ios-step-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d2d3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                </span>
                Selecciona <strong>Agregar a pantalla de inicio</strong>
              </li>
              <li>
                <span className="pwa-ios-step-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d2d3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </span>
                Toca <strong>Agregar</strong> para confirmar
              </li>
            </ol>
            <p className="pwa-ios-note">¡Listo! La app aparecerá en tu pantalla de inicio.</p>
          </div>
        </>
      )}
    </div>
  )
}
